import {
  LegalHeading,
  LegalLinkLine,
  LegalPageFrame,
  LegalParagraph,
} from '@/components/legal/legal-document';
import { PaymentHelpCenterLink } from '@/components/payments/help-center-link';
import { faqJsonLd } from '@/components/seo/faq-list';
import { SeoHead } from '@/components/seo-head';
import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import { SUPPORT_EMAIL, SUPPORT_HOURS, SUPPORT_MAILTO } from '@/constants/support';
import { COMPANY } from '@/content/facts';
import {
  SUPPORT_DESCRIPTION,
  SUPPORT_FAQ,
  SUPPORT_INTRO,
  SUPPORT_PATH,
  SUPPORT_TITLE,
} from '@/content/legal/support';
import { pageTitle } from '@/lib/seo/meta';
import { Ionicons } from '@expo/vector-icons';
import React from 'react';
import { Linking, Pressable, View } from 'react-native';

/** Central de suporte (`/suporte`). Perguntas em `content/legal/support.ts`. */
export default function SupportScreen() {
  const openEmail = () => {
    Linking.openURL(SUPPORT_MAILTO).catch(() => {});
  };

  return (
    <LegalPageFrame
      screenTitle="Suporte"
      head={
        <SeoHead
          title={pageTitle(SUPPORT_TITLE)}
          description={SUPPORT_DESCRIPTION}
          path={SUPPORT_PATH}
          jsonLd={[faqJsonLd(SUPPORT_FAQ)]}
        />
      }
      title={SUPPORT_TITLE}
      intro={SUPPORT_INTRO}
      footerLinks={[
        { label: 'Contato e assuntos', href: '/contato' },
        { label: 'Termos de uso', href: '/terms' },
        { label: 'Política de privacidade', href: '/privacidade' },
      ]}
    >
      <View className="mb-10 gap-4 rounded-2xl border border-border bg-card p-6">
        <LegalHeading>Fale com a gente</LegalHeading>

        <Pressable
          onPress={openEmail}
          accessibilityRole="link"
          className="flex-row items-center gap-3 rounded-xl border border-border bg-background p-4"
        >
          <View className="rounded-full bg-primary/10 p-2">
            <Ionicons name="mail-outline" size={22} color="#FD5001" />
          </View>
          <View className="flex-1">
            <ThemedText className="text-sm opacity-60">E-mail</ThemedText>
            <ThemedText className="font-medium text-base text-primary">{SUPPORT_EMAIL}</ThemedText>
          </View>
          <Ionicons name="chevron-forward" size={18} color="#FD5001" />
        </Pressable>

        <View className="flex-row items-start gap-3">
          <Ionicons name="time-outline" size={20} color="#FD5001" style={{ marginTop: 2 }} />
          <View className="flex-1">
            <ThemedText className="text-sm opacity-60">Horário de atendimento</ThemedText>
            <ThemedText className="text-base opacity-80">{SUPPORT_HOURS}</ThemedText>
          </View>
        </View>

        <ThemedText className="text-sm leading-relaxed opacity-60">
          Respondemos em {COMPANY.supportResponse}. Mande o e-mail da conta e descreva o problema;
          um print da tela ajuda.
        </ThemedText>
      </View>

      <View className="mb-10 gap-4">
        <LegalHeading>Recebimento pelo link</LegalHeading>
        <LegalParagraph>
          Taxas, prazos, limites e reembolso do pagamento pelo link estão na central de recebimentos.
        </LegalParagraph>
        <PaymentHelpCenterLink />
      </View>

      <View className="mb-10">
        <View className="mb-6">
          <LegalHeading>Perguntas frequentes</LegalHeading>
        </View>
        <View className="gap-4">
          {SUPPORT_FAQ.map((item) => (
            <View key={item.question} className="gap-2 rounded-xl border border-border bg-card p-5">
              <ThemedText role="heading" aria-level={3} className="font-semibold text-base">
                {item.question}
              </ThemedText>
              <ThemedText className="text-base leading-relaxed opacity-80">{item.answer}</ThemedText>
            </View>
          ))}
        </View>
        <View className="mt-4 gap-1">
          <LegalLinkLine link={{ label: 'Política de privacidade', url: '/privacidade' }} />
          <LegalLinkLine link={{ label: 'Como excluir a conta', url: '/excluir-conta' }} />
        </View>
      </View>

      <View className="items-center">
        <Button title="Enviar e-mail" onPress={openEmail} className="px-8" />
      </View>
    </LegalPageFrame>
  );
}
